import React from 'react';

/**
 * Round avatar that falls back to gradient initials when no image is set.
 *
 *   <Avatar name="Maria Wang" size={40} />
 *   <Avatar src={user.avatar} name={user.name} size={40} />
 *
 * `gradient` lets callers override the default purple→cyan brand gradient
 * (e.g. to color-code clients in a list). Initials are derived from up to
 * the first two whitespace-separated words.
 */
const DEFAULT_GRADIENT = 'linear-gradient(135deg, var(--primary), var(--secondary))';

/**
 * Deterministic gradient picker. Same input always yields the same
 * gradient — pass a stable seed (email, user id, anything stable
 * per-client) and use the result as the Avatar's `gradient` prop
 * so each client gets a consistent color across the app.
 *
 * Lifted here from AdminClientsPageV2 so the broadcast modal,
 * conversations list, and any future surface that lists clients
 * can use the same palette without re-implementing the hash.
 *
 * Palette uses ONLY the three brand tokens (`--primary`,
 * `--secondary`, `--accent`) so every avatar reflects the
 * trainer's brand. `ThemeProvider` writes these vars from the
 * trainer's `business.colors` (independent trainers) or their
 * organization's colors (gym trainers) on auth load — see
 * `helpers/colorTokens.buildBrandTokens`. Semantic tokens
 * (`--success`, `--warning`, `--error`, `--pink`) are
 * intentionally NOT included: ThemeProvider doesn't override
 * them, so they'd anchor the avatars to Nubod's defaults
 * regardless of the coach's pick.
 *
 * Six ordered pairs from three colors give six distinct
 * gradients at 135° (direction visibly matters at that angle).
 */
const GRADIENTS = [
  'linear-gradient(135deg, var(--primary), var(--secondary))',
  'linear-gradient(135deg, var(--secondary), var(--accent))',
  'linear-gradient(135deg, var(--accent), var(--primary))',
  'linear-gradient(135deg, var(--primary), var(--accent))',
  'linear-gradient(135deg, var(--secondary), var(--primary))',
  'linear-gradient(135deg, var(--accent), var(--secondary))',
];

export function gradientFor(seed) {
  if (!seed) return GRADIENTS[0];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
}

function initialsOf(name) {
  if (!name) return '?';
  return (
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase() || '?'
  );
}

export default function Avatar({
  name = '',
  src,
  size = 32,
  gradient = DEFAULT_GRADIENT,
  className = '',
  alt,
}) {
  const initials = initialsOf(name);
  const style = {
    width: size,
    height: size,
    fontSize: Math.max(11, Math.round(size * 0.38)),
    background: src ? 'transparent' : gradient,
  };
  return (
    <div className={`av-circle ${className}`.trim()} style={style}>
      {src ? (
        <img
          src={src}
          alt={alt || name || 'avatar'}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
      ) : (
        initials
      )}
    </div>
  );
}
